import { useEffect } from "react"
import { HomeRegistrationFragment } from "@pathwright/graphql-client/src/generated"
import { usePositioningContext } from "../../positioning/PositioningContext"

// We'll use this util to "migrate" registrations to the new positioning system.
// This is only necessary to maintain similar ordering used on the old home screen.
// The old home screen orders most recent first with a separate section for completed
// registrations. The new home screen orders most recent last with no respect for
// completed registrations.
function SortRegistrations({
  registrations
}: {
  registrations: HomeRegistrationFragment[]
}) {
  const {
    updatePositionedObject,
    positioningContext,
    positioningContextQuery
  } = usePositioningContext()

  const hasPositionedHomeScreen = !!positioningContext?.id
  const shouldSort =
    !!registrations.length &&
    !positioningContextQuery.loading &&
    !hasPositionedHomeScreen

  useEffect(() => {
    if (shouldSort) {
      // First we sort the registrations according to the old home screen order.
      const sortedRegistrations = registrations.sort((a, b) => {
        // Sort by non-completed registrations first, then by most recent.
        if (a.completion_state !== 3 && b.completion_state === 3) return -1
        if (a.completion_state === 3 && b.completion_state !== 3) return 1
        // If both are completed or not completed, sort by most recent.
        return new Date(b.created).getTime() - new Date(a.created).getTime()
      })

      // Now, we assign positions to the registrations to enforce the new sort order (as a default).
      // We position the last item to the last index, which will generate positions for all preceding
      // items as well.
      const index = sortedRegistrations.length - 1
      updatePositionedObject({
        list: sortedRegistrations,
        item: sortedRegistrations[index],
        index: index,
        // We refetch the positioning context when sorting is complete to reasses if
        // a hasPositionedHomeScreen.
        onCompleted: positioningContextQuery.refetch
      })
    }
  }, [shouldSort])

  return null
}

export default SortRegistrations
